import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { format, subMonths, startOfYear, endOfYear, startOfMonth, endOfMonth, parseISO } from 'date-fns'
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
  LabelList,
} from 'recharts'
import {
  DollarSign, Briefcase, TrendingUp, Users, BarChart3,
} from 'lucide-react'

// ─── Palette ─────────────────────────────────────────────────────────────────

const NAVY   = '#0F1F3D'
const GREEN  = '#2A9D5C'
const AMBER  = '#E8A020'
const SLATE  = '#64748B'
const BLUE   = '#3B82F6'
const PURPLE = '#8B5CF6'
const TEAL   = '#06B6D4'
const PINK   = '#EC4899'

const CHART_COLORS = [GREEN, BLUE, AMBER, PURPLE, TEAL, PINK, SLATE, NAVY]

const EVENT_TYPE_COLORS = {
  wedding:   GREEN,
  corporate: BLUE,
  birthday:  AMBER,
  festival:  PURPLE,
  community: TEAL,
  other:     SLATE,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  return '$' + parseFloat(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function fmtMoneyFull(n) {
  return '$' + parseFloat(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : s
}

// Custom tooltip for money charts
function MoneyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{
      background: 'white', border: '1px solid #E2E8F0', borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,.10)',
      fontSize: 13,
    }}>
      <div style={{ fontWeight: 700, color: NAVY, marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: <strong>{fmtMoney(p.value)}</strong>
        </div>
      ))}
    </div>
  )
}

function CountTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{
      background: 'white', border: '1px solid #E2E8F0', borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,.10)',
      fontSize: 13,
    }}>
      <div style={{ fontWeight: 700, color: NAVY, marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  )
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="card" style={{ padding: '18px 22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
            {label}
          </div>
          <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 800, color: accent || NAVY, lineHeight: 1.1 }}>
            {value}
          </div>
          {sub && (
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 5 }}>
              {sub}
            </div>
          )}
        </div>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: `${accent || NAVY}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={18} style={{ color: accent || NAVY }} />
        </div>
      </div>
    </div>
  )
}

// ─── Section card wrapper ─────────────────────────────────────────────────────

function Section({ title, subtitle, children, action }) {
  return (
    <div className="card" style={{ overflow: 'hidden', marginBottom: 24 }}>
      <div className="card-header" style={{ padding: '16px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 'var(--text-md)', color: NAVY }}>{title}</div>
          {subtitle && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      <div style={{ padding: '12px 22px 22px' }}>
        {children}
      </div>
    </div>
  )
}

// ─── ReportsPage ──────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const [range,    setRange]    = useState('this_year')
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState('')
  const [kpis,     setKpis]     = useState({})
  const [monthly,  setMonthly]  = useState([])
  const [byType,   setByType]   = useState([])
  const [leadFunnel, setLeadFunnel] = useState([])
  const [invUsage, setInvUsage] = useState([])
  const [outstanding, setOutstanding] = useState([])

  // ── Date range helpers ────────────────────────────────────────────────────

  function getRangeDates() {
    const now = new Date()
    if (range === 'this_year') {
      return { start: format(startOfYear(now), 'yyyy-MM-dd'), end: format(endOfYear(now), 'yyyy-MM-dd') }
    }
    if (range === 'last_year') {
      const ly = new Date(now.getFullYear() - 1, 0, 1)
      return { start: format(startOfYear(ly), 'yyyy-MM-dd'), end: format(endOfYear(ly), 'yyyy-MM-dd') }
    }
    // Last 12 months
    return { start: format(subMonths(now, 11), 'yyyy-MM-01'), end: format(endOfMonth(now), 'yyyy-MM-dd') }
  }

  // ── Main data load ────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const { start, end } = getRangeDates()

    try {
      const [jobsRes, allJobsRes, leadsRes, invRes] = await Promise.all([
      // Jobs in range
      supabase.from('jobs')
        .select('id, event_date, event_type, status, deposit_required, deposit_received, balance_due, payment_status, clients(name, id)')
        .gte('event_date', start)
        .lte('event_date', end)
        .neq('status', 'cancelled'),

      // All non-cancelled jobs for inventory usage (all time)
      supabase.from('job_inventory')
        .select('quantity_reserved, inventory_categories(id, name)')
        .not('inventory_categories', 'is', null),

      // All leads (for funnel — not range-filtered)
      supabase.from('leads').select('status'),

      // Outstanding balances (all active jobs with balance_due > 0)
      supabase.from('jobs')
        .select('id, event_date, event_type, deposit_required, deposit_received, balance_due, payment_status, clients(name, id)')
        .gt('balance_due', 0)
        .not('status', 'in', '("cancelled","completed")')
        .order('event_date')
        .limit(20),
    ])

      const firstError = [jobsRes, allJobsRes, leadsRes, invRes].find(r => r?.error)?.error
      if (firstError) throw firstError

      const jobs = jobsRes.data || []

    // ── KPIs ────────────────────────────────────────────────────────────────

    const totalBooked    = jobs.reduce((s, j) => s + (parseFloat(j.deposit_required) || 0), 0)
    const totalCollected = jobs.reduce((s, j) => s + (parseFloat(j.deposit_received) || 0), 0)
    const totalJobs      = jobs.length
    const avgJobValue    = totalJobs > 0 ? totalBooked / totalJobs : 0
    const completedJobs  = jobs.filter(j => j.status === 'completed').length

    const leads     = leadsRes.data || []
    const converted = leads.filter(l => l.status === 'converted').length
    const convRate  = leads.length > 0 ? Math.round((converted / leads.length) * 100) : 0

    setKpis({ totalBooked, totalCollected, totalJobs, avgJobValue, completedJobs, convRate, totalLeads: leads.length })

    // ── Monthly revenue ───────────────────────────────────────────────────

    const monthMap = {}
    // Build all months in range
    const startD = parseISO(start)
    const endD   = parseISO(end)
    let cur = startOfMonth(startD)
    while (cur <= endD) {
      const key = format(cur, 'yyyy-MM')
      monthMap[key] = { month: format(cur, 'MMM yy'), booked: 0, collected: 0, jobs: 0 }
      cur = startOfMonth(new Date(cur.getFullYear(), cur.getMonth() + 1, 1))
    }
    jobs.forEach(j => {
      const key = j.event_date?.slice(0, 7)
      if (key && monthMap[key]) {
        monthMap[key].booked    += parseFloat(j.deposit_required) || 0
        monthMap[key].collected += parseFloat(j.deposit_received) || 0
        monthMap[key].jobs      += 1
      }
    })
    setMonthly(Object.values(monthMap))

    // ── By event type ─────────────────────────────────────────────────────

    const typeMap = {}
    jobs.forEach(j => {
      const t = j.event_type || 'other'
      if (!typeMap[t]) typeMap[t] = { type: capitalize(t), booked: 0, jobs: 0, rawType: t }
      typeMap[t].booked += parseFloat(j.deposit_required) || 0
      typeMap[t].jobs   += 1
    })
    setByType(
      Object.values(typeMap)
        .sort((a, b) => b.booked - a.booked)
    )

    // ── Lead funnel ───────────────────────────────────────────────────────

    const ORDER = ['new', 'contacted', 'quoted', 'converted', 'lost']
    const LABELS = { new: 'New', contacted: 'Contacted', quoted: 'Quoted', converted: 'Converted', lost: 'Lost' }
    const COLORS_FUNNEL = [BLUE, PURPLE, AMBER, GREEN, SLATE]
    const lMap = {}
    leads.forEach(l => { lMap[l.status] = (lMap[l.status] || 0) + 1 })
    setLeadFunnel(
      ORDER.map((s, i) => ({
        status: LABELS[s],
        count: lMap[s] || 0,
        fill: COLORS_FUNNEL[i],
      }))
    )

    // ── Inventory usage ───────────────────────────────────────────────────

    const invMap = {}
    ;(allJobsRes.data || []).forEach(ji => {
      const name = ji.inventory_categories?.name || 'Unknown'
      invMap[name] = (invMap[name] || 0) + (ji.quantity_reserved || 0)
    })
    setInvUsage(
      Object.entries(invMap)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 8)
    )

    // ── Outstanding balances ──────────────────────────────────────────────

      setOutstanding(invRes.data || [])
    } catch (err) {
      console.error('Failed to load reports:', err)
      setError(err?.message || 'Failed to load reports data.')
      setKpis({})
      setMonthly([])
      setByType([])
      setLeadFunnel([])
      setInvUsage([])
      setOutstanding([])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range])

  useEffect(() => { load() }, [load])

  // ── Render ───────────────────────────────────────────────────────────────

  const rangeLabel = range === 'this_year' ? 'This Year'
    : range === 'last_year' ? 'Last Year'
    : 'Last 12 Months'

  const RangeSelector = (
    <div style={{ display: 'flex', gap: 6 }}>
      {[
        { key: 'this_year',    label: 'This Year' },
        { key: 'last_year',    label: 'Last Year' },
        { key: 'last_12',      label: 'Last 12 Mo' },
      ].map(({ key, label }) => (
        <button key={key}
          className={`btn btn-sm ${range === key ? 'btn-primary' : 'btn-ghost'}`}
          style={{ borderRadius: 6 }}
          onClick={() => setRange(key)}>
          {label}
        </button>
      ))}
    </div>
  )

  return (
    <div>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Reports &amp; Analytics</h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', marginTop: 2 }}>
            {rangeLabel} — WTR business overview
          </p>
        </div>
        {RangeSelector}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px', color: 'var(--color-text-muted)' }}>
          <BarChart3 size={36} style={{ marginBottom: 12 }} />
          <div>Loading reports…</div>
        </div>
      ) : (
        <>
          {/* ── KPI Cards ───────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            <KpiCard
              label="Total Booked"
              value={fmtMoney(kpis.totalBooked)}
              sub={`${kpis.totalJobs} jobs · ${rangeLabel.toLowerCase()}`}
              icon={DollarSign}
              accent={GREEN}
            />
            <KpiCard
              label="Collected"
              value={fmtMoney(kpis.totalCollected)}
              sub={`${Math.round(kpis.totalBooked > 0 ? (kpis.totalCollected / kpis.totalBooked) * 100 : 0)}% of booked`}
              icon={TrendingUp}
              accent={BLUE}
            />
            <KpiCard
              label="Avg Job Value"
              value={fmtMoney(kpis.avgJobValue)}
              sub={`${kpis.completedJobs} completed`}
              icon={Briefcase}
              accent={NAVY}
            />
            <KpiCard
              label="Lead Conversion"
              value={`${kpis.convRate}%`}
              sub={`${kpis.totalLeads} leads total`}
              icon={Users}
              accent={AMBER}
            />
          </div>

          {/* ── Revenue by Month ─────────────────────────────────────── */}
          <Section
            title="Revenue by Month"
            subtitle={`Booked vs. collected — ${rangeLabel.toLowerCase()}`}
          >
            {monthly.length === 0 || monthly.every(m => m.booked === 0) ? (
              <EmptyChart message="No job data for this period." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthly} margin={{ top: 4, right: 8, bottom: 0, left: 8 }} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: SLATE }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: SLATE }} axisLine={false} tickLine={false}
                    tickFormatter={v => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`} />
                  <Tooltip content={<MoneyTooltip />} cursor={{ fill: '#F8FAFC' }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                  <Bar dataKey="booked"    name="Booked"    fill={GREEN} radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="collected" name="Collected" fill={BLUE}  radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Section>

          {/* ── Two-column row: by type + lead funnel ─────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>

            {/* Revenue by Event Type */}
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: '16px 22px' }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-md)', color: NAVY }}>Revenue by Event Type</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{rangeLabel}</div>
              </div>
              <div style={{ padding: '12px 22px 22px' }}>
                {byType.length === 0 ? (
                  <EmptyChart message="No data for this period." />
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={byType} layout="vertical" margin={{ top: 0, right: 60, bottom: 0, left: 0 }} barSize={18}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: SLATE }} axisLine={false} tickLine={false}
                        tickFormatter={v => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`} />
                      <YAxis type="category" dataKey="type" tick={{ fontSize: 12, fill: SLATE }} axisLine={false} tickLine={false} width={72} />
                      <Tooltip content={<MoneyTooltip />} cursor={{ fill: '#F8FAFC' }} />
                      <Bar dataKey="booked" name="Booked" radius={[0, 4, 4, 0]}>
                        {byType.map((entry, i) => (
                          <Cell key={i} fill={EVENT_TYPE_COLORS[entry.rawType] || CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                        <LabelList dataKey="jobs"
                          position="right"
                          formatter={v => `${v} job${v !== 1 ? 's' : ''}`}
                          style={{ fontSize: 10, fill: SLATE }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Lead Funnel */}
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: '16px 22px' }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-md)', color: NAVY }}>Lead Pipeline</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>All time — current status breakdown</div>
              </div>
              <div style={{ padding: '12px 22px 22px' }}>
                {leadFunnel.every(f => f.count === 0) ? (
                  <EmptyChart message="No lead data yet." />
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={leadFunnel} layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 0 }} barSize={18}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: SLATE }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="status" tick={{ fontSize: 12, fill: SLATE }} axisLine={false} tickLine={false} width={72} />
                      <Tooltip content={<CountTooltip />} cursor={{ fill: '#F8FAFC' }} />
                      <Bar dataKey="count" name="Leads" radius={[0, 4, 4, 0]}>
                        {leadFunnel.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                        <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 700, fill: SLATE }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* ── Two-column row: inventory + jobs trend ─────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>

            {/* Top Inventory Usage */}
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: '16px 22px' }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-md)', color: NAVY }}>Top Inventory Usage</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>Units reserved across all jobs</div>
              </div>
              <div style={{ padding: '12px 22px 22px' }}>
                {invUsage.length === 0 ? (
                  <EmptyChart message="No inventory data yet." />
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={invUsage} layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 0 }} barSize={18}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: SLATE }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: SLATE }} axisLine={false} tickLine={false} width={120} />
                      <Tooltip content={<CountTooltip />} cursor={{ fill: '#F8FAFC' }} />
                      <Bar dataKey="total" name="Units Reserved" fill={TEAL} radius={[0, 4, 4, 0]}>
                        <LabelList dataKey="total" position="right" style={{ fontSize: 11, fontWeight: 700, fill: SLATE }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Jobs per Month (count) */}
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: '16px 22px' }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-md)', color: NAVY }}>Job Volume by Month</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{rangeLabel}</div>
              </div>
              <div style={{ padding: '12px 22px 22px' }}>
                {monthly.every(m => m.jobs === 0) ? (
                  <EmptyChart message="No jobs for this period." />
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={monthly} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: SLATE }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: SLATE }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<CountTooltip />} cursor={{ stroke: '#E2E8F0' }} />
                      <Line
                        type="monotone" dataKey="jobs" name="Jobs"
                        stroke={PURPLE} strokeWidth={2.5}
                        dot={{ r: 4, fill: PURPLE, strokeWidth: 0 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* ── Outstanding Balances ─────────────────────────────────── */}
          <Section
            title="Outstanding Balances"
            subtitle="Active jobs with unpaid amounts"
          >
            {outstanding.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                ✓ No outstanding balances — all active jobs are paid up!
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                      {['Client', 'Event Date', 'Type', 'Required', 'Received', 'Balance', 'Status'].map(h => (
                        <th key={h} style={{
                          textAlign: 'left', padding: '8px 12px',
                          fontSize: 'var(--text-xs)', textTransform: 'uppercase',
                          letterSpacing: '.04em', color: 'var(--color-text-muted)',
                          fontWeight: 700, whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {outstanding.map((j, i) => {
                      const psMap = {
                        pending:      { bg: '#F3F4F6', color: '#6B7280' },
                        deposit_paid: { bg: '#FEF3C7', color: '#92400E' },
                        overdue:      { bg: '#FEE2E2', color: '#991B1B' },
                        paid_in_full: { bg: '#D1FAE5', color: '#065F46' },
                      }
                      const ps = psMap[j.payment_status] || psMap.pending
                      return (
                        <tr key={j.id} style={{
                          borderBottom: '1px solid var(--color-border)',
                          background: i % 2 === 0 ? 'white' : '#FAFBFC',
                        }}>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: NAVY }}>
                            {j.clients?.name || '—'}
                          </td>
                          <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                            {j.event_date || '—'}
                          </td>
                          <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)' }}>
                            {capitalize(j.event_type) || '—'}
                          </td>
                          <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                            {fmtMoneyFull(j.deposit_required)}
                          </td>
                          <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: GREEN }}>
                            {fmtMoneyFull(j.deposit_received)}
                          </td>
                          <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', fontWeight: 700, color: '#B91C1C' }}>
                            {fmtMoneyFull(j.balance_due)}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{
                              fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: 10,
                              background: ps.bg, color: ps.color, fontWeight: 600, whiteSpace: 'nowrap',
                            }}>
                              {capitalize(j.payment_status)}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--color-border)' }}>
                      <td colSpan={5} style={{ padding: '10px 12px', fontWeight: 700, color: NAVY, fontSize: 'var(--text-sm)' }}>
                        Total Outstanding
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 800, color: '#B91C1C' }}>
                        {fmtMoneyFull(outstanding.reduce((s, j) => s + (parseFloat(j.balance_due) || 0), 0))}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  )
}

function EmptyChart({ message }) {
  return (
    <div style={{
      height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', flexDirection: 'column', gap: 8,
    }}>
      <BarChart3 size={28} style={{ opacity: 0.35 }} />
      {message}
    </div>
  )
}
